/**
 * QuoteDocument — the customer-facing document.
 *
 * PR-02c reference: plan §1c.1
 * Wire: QUOTING_PACK/Wires/QR-1-quote-review-send.html · R1 (live preview) + R5 (sent-quote)
 *
 * ONE COMPONENT, TWO OUTPUTS.
 *   - On screen: the left column of Review & Send renders it as a preview.
 *   - On print: the SAME component is what `window.print()` outputs. A print
 *     stylesheet at the bottom of this file suppresses everything else on the
 *     page and enforces page-break rules.
 *   - Sent-quote view (wire R5): renders identically \u2014 a sent quote must show
 *     what the customer received, not a re-derivation from current data.
 *
 * COST/MARGIN ISOLATION (plan \u00a74).
 *   This component receives a `viewQuote` prop only. `viewQuote` is a strict
 *   customer-only projection built in QuoteReviewSend BEFORE it hands off:
 *     - Per-line: name, description, area, qty, unit label, amount (customer price).
 *     - Per accessory: name, area, qty, unit label, amount \u2014 ONLY if rollUp is false.
 *     - Totals: subtotal, discount, tax, total (customer-visible only).
 *   The internal cost buckets (materialCost / laborCost / otherCost), the margin
 *   rate, the cost breakdown, the internal notes, and the recipients list are
 *   NEVER passed to this component. If it is not in `viewQuote`, it cannot be
 *   rendered by accident.
 *
 * DISPLAY OPTIONS.
 *   The 8 toggles (groupByArea, showSqft, lineItemPricing, laborPricing, showTax,
 *   notes, headlineNotes, attachments) each conditionally hide or show part of
 *   the document. Notes and prices print unless toggled off. They are customer-facing.
 *
 * ALTERNATES (IDI-131, supersedes plan \u00a72's exclusion): rendered DIRECTLY UNDER its parent line
 * in the scope table, labelled "Instead of <parent>" with its signed Cost
 * Add/Deduct (10-02 co-UAT ruling). No bottom Alternates section on the customer
 * document (Quote Analysis keeps its own sections, IDI-444). Alternates never
 * participate in any total.
 */

import React from 'react';
import './QuoteDocument.print.css';
import { EmptyState } from '../ui/EmptyState';
import { Alert } from '../ui/Alert';
import { optionDisplayParts } from '../../quote-builder/optionDisplayParts';
import { sanitizeTcHtml } from '../../utils/sanitizeTcHtml';

// ---------- helpers ----------

function fmtCents(cents) {
  const n = Number(cents) || 0;
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}



// Group lines by area. Preserves within-area order.
function groupByAreaOrPassthrough(lines, groupByArea) {
  if (!groupByArea) return [{ areaId: null, areaLabel: null, lines }];
  const groups = new Map();
  const order = [];
  for (const line of lines) {
    const key = line.areaId || '__uncategorized__';
    if (!groups.has(key)) {
      groups.set(key, { areaId: line.areaId || null, areaLabel: line.areaLabel || null, lines: [] });
      order.push(key);
    }
    groups.get(key).lines.push(line);
  }
  return order.map((k) => groups.get(k));
}

// ---------- subcomponents ----------

/**
 * DocumentHeader — the canonical document header used on Quote / Invoice /
 * Change Order documents and on their review-and-send previews.
 *
 * PR-36 (2026-08-17) canonical vocabulary. All branding fields are SNAKE_CASE,
 * matching BrandingPage's write (the writer) and this component's read:
 *
 *   branding.logo_url            branding.primary_color
 *   branding.secondary_color     branding.text_color
 *   branding.logo_position       branding.email_signature
 *   branding.include_logo_in_signature   branding.signature_logo_size
 *
 * There is no fallback reader on this component. If a value arrives spelled
 * differently (e.g. the onboarding wizard's `branding.logo`) the field will
 * render its placeholder — that is the correct behaviour, and the writer is
 * where the mismatch gets fixed (PR-42 for the wizard). See useBranding.js
 * for the fuller vocabulary block.
 *
 * PR-36 §1.2 (GP-09): logo_position is now honoured. Previously written by
 * BrandingPage, read by nobody. Three positions:
 *
 *   'left'    logo + company block on the left, doc meta on the right (default)
 *   'right'   logo + company block on the right, doc meta on the left
 *   'center'  logo + company block centered above; doc meta below on the right
 */
export function DocumentHeader({ branding, profile, meta = {}, docType = 'QUOTE' }) {
  const accent = branding?.primary_color || 'var(--color-action-primary)';
  const companyName = profile?.company_name || 'Your Company';
  const addressLine = [profile?.address_city, profile?.address_state, profile?.address_zip]
    .filter(Boolean).join(' ');
  const position = branding?.logo_position === 'right' || branding?.logo_position === 'center'
    ? branding.logo_position
    : 'left';

  const identityBlock = (
    <div className="qd-header-identity">
      {branding?.logo_url ? (
        <img src={branding.logo_url} alt="" className="qd-logo-img" />
      ) : (
        <div className="qd-logo-placeholder" aria-hidden="true">
          <span>LOGO</span>
        </div>
      )}
      <div>
        {/* Tenant accent color applies to name and logo ONLY — wire "tenant accent — name and logo only". */}
        <div className="qd-company-name" style={{ color: accent }}>{companyName}</div>
        <div className="qd-company-address">
          {profile?.address_street && <>{profile.address_street}<br /></>}
          {addressLine && <>{addressLine}<br /></>}
          {profile?.phone && <>{profile.phone}<br /></>}
          {profile?.email && <>{profile.email}</>}
        </div>
      </div>
    </div>
  );

  const metaBlock = (
    <div className="qd-meta">
      <b>{docType} {meta.quoteNumber || ''}</b>
      {meta.date && <><br />Date {meta.date}</>}
      {meta.validUntil && <><br />Valid until {meta.validUntil}</>}
      {meta.salesperson && <><br />Salesperson {meta.salesperson}</>}
    </div>
  );

  return (
    <div className={`qd-header qd-header--${position}`}>
      {identityBlock}
      {metaBlock}
    </div>
  );
}

function PartiesBlock({ billTo, jobAddress, sameAsBilling, paymentTerms, paymentType }) {
  return (
    <div className="qd-parties">
      <div>
        <div className="qd-plab">Bill to</div>
        <div className="qd-pval">
          {billTo?.name && <><b>{billTo.name}</b><br /></>}
          {billTo?.contactName && <>{billTo.contactName}<br /></>}
          {billTo?.street && <>{billTo.street}<br /></>}
          {billTo?.street2 && <>{billTo.street2}<br /></>}
          {(billTo?.city || billTo?.state || billTo?.zip) && (
            <>{[billTo.city, billTo.state, billTo.zip].filter(Boolean).join(' ')}<br /></>
          )}
          {billTo?.phone && <>{billTo.phone}</>}
        </div>
      </div>
      <div>
        <div className="qd-plab">Job address</div>
        <div className="qd-pval">
          {sameAsBilling ? (
            <>
              Same as billing
              {(billTo?.street || billTo?.city) && (
                <><br /><span className="qd-mut">
                  {[billTo?.street, [billTo?.city, billTo?.state].filter(Boolean).join(' '), billTo?.zip].filter(Boolean).join(', ')}
                </span></>
              )}
            </>
          ) : (
            <>
              {jobAddress?.street && <>{jobAddress.street}<br /></>}
              {(jobAddress?.city || jobAddress?.state || jobAddress?.zip) &&
                <>{[jobAddress.city, jobAddress.state, jobAddress.zip].filter(Boolean).join(' ')}</>}
            </>
          )}
        </div>
        {paymentTerms && (
          <>
            <div className="qd-plab" style={{ marginTop: 11 }}>Payment terms</div>
            <div className="qd-pval">{paymentTerms}</div>
          </>
        )}
        {paymentType && (
          <>
            <div className="qd-plab" style={{ marginTop: 11 }}>Payment method</div>
            <div className="qd-pval">{paymentType}</div>
          </>
        )}
      </div>
    </div>
  );
}

function ScopeTable({ groups, showSqft, showLineItemPricing, showLaborPricing, showAreaColumn = true }) {
  return (
    <>
      <div className="qd-docsec">Scope of work</div>
      <table className="qd-li">
        <thead>
          <tr>
            {/* PT-15 #8c (2026-07-24): the Qty header used to render unconditionally
                while the Qty <td> was gated on `showSqft`. That left an empty
                column with an orphaned header when the toggle was off. Both
                are now gated together. */}
            <th style={{ width: '52%' }}>Item</th>
            {/* PR-32 (308_ §4): hide the Area column when Group by area is ON.
                Every row would otherwise restate the group header it sits under. */}
            {showAreaColumn && <th>Area</th>}
            {showSqft && <th className="qd-n">Qty</th>}
            {showLineItemPricing && <th className="qd-n">Amount</th>}
          </tr>
        </thead>
        <tbody>
          {groups.map((group, gIdx) => (
            <React.Fragment key={group.areaId || `g${gIdx}`}>
              {group.areaLabel && (
                <tr className="qd-areahdr"><td colSpan={1 + (showAreaColumn ? 1 : 0) + (showSqft ? 1 : 0) + (showLineItemPricing ? 1 : 0)}>{group.areaLabel}</td></tr>
              )}
              {group.lines.map((line) => (
                <React.Fragment key={line.id}>
                  {/* PR-02c print rule: line + its child accessory rows must not split
                      across a page break. The `.qd-line-group` <tbody>-like grouping
                      is achieved via `break-inside: avoid` on each row and its
                      immediate accessory row \u2014 details in QuoteDocument.print.css. */}
                  <tr className="qd-line">
                    <td>
                      {/* IDI-114 / IDI-131: the dealer's per-line Alias is the
                          customer-facing name when set. */}
                      <b>{line.alias || line.name}</b>
                      {line.description && <div className="qd-desc">{line.description}</div>}
                    </td>
                    {showAreaColumn && <td>{line.subAreaLabel || line.areaLabel || ''}</td>}
                    {showSqft && (
                      <td className="qd-n">
                        {line.qty != null
                          ? `${line.qty.toLocaleString()}${line.unit ? ` ${line.unit}` : ''}`
                          : ''}
                      </td>
                    )}
                    {showLineItemPricing && (
                      <td className="qd-n">{fmtCents(line.amount)}</td>
                    )}
                  </tr>

                  {/* Itemised accessories \u2014 rolled-up accessories appear NOWHERE (plan \u00a72). */}
                  {line.itemisedAccessories && line.itemisedAccessories.map((acc) => (
                    <tr className="qd-child" key={acc.id}>
                      <td>{acc.name}</td>
                      {showAreaColumn && <td>{acc.areaLabel || line.subAreaLabel || line.areaLabel || ''}</td>}
                      {showSqft && (
                        <td className="qd-n">
                          {acc.qty != null ? `${acc.qty.toLocaleString()}${acc.unit ? ` ${acc.unit}` : ''}` : ''}
                        </td>
                      )}
                      {showLineItemPricing && (
                        <td className="qd-n">{fmtCents(acc.amount)}</td>
                      )}
                    </tr>
                  ))}

                  {/* Optional labor-pricing breakdown row per line (opt-in via toggle).
                      Only shown when the toggle is on AND the line carries a `laborAmount`. */}
                  {showLaborPricing && line.laborAmount != null && (
                    <tr className="qd-child" key={`${line.id}-labor`}>
                      <td>Labor</td>
                      {showAreaColumn && <td>{line.subAreaLabel || line.areaLabel || ''}</td>}
                      {showSqft && <td className="qd-n"></td>}
                      {showLineItemPricing && (
                        <td className="qd-n">{fmtCents(line.laborAmount)}</td>
                      )}
                    </tr>
                  )}

                  {/* IDI-131 / IDI-545: alternates sit directly under their parent
                      line, labelled "Alternate: <product>", with the SIGNED Cost
                      Add/Deduct vs that parent. Never part of any total. */}
                  {Array.isArray(line.alternates) && line.alternates.map((alt) => (
                    <tr
                      className="qd-child qd-alt"
                      key={`${line.id}:alt:${alt.id}`}
                      data-testid="quote-document-alternate-row"
                      data-parent-id={line.id}
                    >
                      <td>
                        <span className="qd-alt-label qd-mut">Alternate:</span>{' '}
                        <b>{alt.name || 'Alternate'}</b>
                        {alt.description && <div className="qd-desc">{alt.description}</div>}
                      </td>
                      {showAreaColumn && <td>{alt.subAreaLabel || alt.areaLabel || ''}</td>}
                      {showSqft && (
                        <td className="qd-n">
                          {alt.qty != null ? `${alt.qty.toLocaleString()}${alt.unit ? ` ${alt.unit}` : ''}` : ''}
                        </td>
                      )}
                      {showLineItemPricing && (
                        <td className="qd-n qd-alt-delta" title="Cost Add/Deduct">{fmtSignedCents(alt.delta)}</td>
                      )}
                    </tr>
                  ))}

                  {/* Per-line notes (opt-in via `notes` toggle). Customer-facing. */}
                  {line.notes && (
                    <tr className="qd-notes-row">
                      <td colSpan={2 + (showSqft ? 1 : 0) + (showLineItemPricing ? 1 : 0)} className="qd-line-notes">{line.notes}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

function TotalsBlock({ subtotal, discount, discounts, tax, taxLabel, total, showTax }) {
  // PR-4 §1b: prefer the per-discount `discounts[]` array (one row per entry
  // with its description) over a single collapsed scalar. Falls back to the
  // scalar when the array is absent, preserving legacy behavior.
  const rows = Array.isArray(discounts) && discounts.length > 0
    ? discounts
    : (discount > 0 ? [{ id: 'legacy', label: '', amount: discount }] : []);
  return (
    <div className="qd-totals">
      <div className="qd-trow"><span className="qd-mut">Subtotal</span><span className="qd-v">{fmtCents(subtotal)}</span></div>
      {rows.map((d) => (
        <div key={d.id || d.label || 'discount'} className="qd-trow">
          <span className="qd-mut">{d.label ? `Discount \u00b7 ${d.label}` : 'Discount'}</span>
          <span className="qd-v">−{fmtCents(d.amount)}</span>
        </div>
      ))}
      {showTax && (
        <div className="qd-trow">
          <span className="qd-mut">{taxLabel || 'Tax'}</span>
          <span className="qd-v">{fmtCents(tax)}</span>
        </div>
      )}
      <div className="qd-trow qd-gt"><span>Total</span><span className="qd-v">{fmtCents(total)}</span></div>
    </div>
  );
}

// ---------- Signed Cost Add/Deduct (IDI-131) ----------
// Each alternate is a substitute for its parent line, so the number shown is
// the SIGNED difference from that line (negative = cheaper), not a price.
export function fmtSignedCents(cents) {
  const n = Math.round(Number(cents) || 0);
  if (n === 0) return fmtCents(0);
  return `${n < 0 ? '−' : '+'}${fmtCents(Math.abs(n))}`;
}

// IDI-545: optionDisplayParts lives in quote-builder/optionDisplayParts so the
// signing page shares it; re-exported here for existing callers and tests.
export { optionDisplayParts };

// ---------- OptionsBlock (PR-4 §2 · wire QB-OPT O3 · IDI-545) ----------
// Optional upgrades render BELOW the totals in their own bordered section.
// Each reads "<heading> — <alias>" / "<product>" / "<note>", with "+$x" on the
// right of the last line. The `+` signals they are NOT in the quoted total.
function OptionsBlock({ options }) {
  if (!Array.isArray(options) || options.length === 0) return null;
  return (
    <div className="qd-optbox" data-testid="quote-document-options">
      <div className="qd-opthead">Optional upgrades — not included in the total above</div>
      {options.map((o) => {
        const { title, product } = optionDisplayParts(o);
        return (
          <div key={o.id || o.name} className="qd-optrow" data-testid="quote-document-option-row">
            <div className="qd-optbody">
              {title && (
                <div className="qd-optheading" data-testid="quote-document-option-heading">
                  <b>{title}</b>
                </div>
              )}
              {product && (
                <div className="qd-optname" data-testid="quote-document-option-name">
                  {title ? product : <b>{product}</b>}
                </div>
              )}
              {o.notes && (
                <div className="qd-optnote qd-mut" data-testid="quote-document-option-note">{o.notes}</div>
              )}
              {o.description && <div className="qd-optdesc qd-mut">{o.description}</div>}
            </div>
            <div className="qd-optamt">{`+${fmtCents(o.amount)}`}</div>
          </div>
        );
      })}
      <div className="qd-optfoot qd-mut">
        Let your estimator know if you'd like this added — it is not part of the quoted total.
      </div>
    </div>
  );
}

// ---------- main ----------

/**
 * @param {object}  props
 * @param {object}  props.viewQuote       Customer-only projection (see comment at top).
 * @param {object}  props.options         Display toggles (all 8 keys).
 * @param {object}  [props.branding]      { logo_url, primary_color }
 * @param {object}  [props.profile]       { company_name, phone, email, address_* }
 */
export default function QuoteDocument({ viewQuote, options, branding, profile }) {
  if (!viewQuote) return null;

  const {
    quoteNumber,
    date,
    validUntil,
    salesperson,
    billTo,
    jobAddress,
    sameAsBilling,
    paymentTerms,
    paymentType,
    lines,
    options: optionItems,
    subtotal,
    discount,
    discounts,
    tax,
    taxLabel,
    total,
    // PT-15 #8d (2026-07-24): headlineNotes destructure removed alongside the toggle.
    // PR-20 (2026-07-22): general customer notes block. Rendered between
    // Totals/Options and Terms when the display toggle is on (default). This
    // is the "Notes" section referenced by the wire QR-1 R1.
    notes,
    terms: rawTerms,
    attachments,
  } = viewQuote;
  // IDI-624 (D1/D2/D5): stored T&C HTML is cleaned to the allowlist; '' when
  // nothing visible survives, so no "Terms & conditions" section renders.
  // This is the single sanitize site; the render below uses `terms` as-is.
  const terms = sanitizeTcHtml(rawTerms);
  const opt = options || {};
  const groups = groupByAreaOrPassthrough(lines || [], opt.groupByArea !== false);

  // DEF-088 (W1.1a/W1.1b): gate line-item + totals rendering on presence of
  // actual lines. Prevents the DEF-081-shape defect where an empty <thead> and
  // a bare totals block render above zero rows when a stale non-zero subtotal
  // remains on the record. Internal preview surface — shows the subtotal
  // AS STORED so operators see the data as it exists; a warning notice makes
  // the contradiction visible instead of silent.
  const hasLines = Array.isArray(lines) && lines.length > 0;
  const subtotalCents = Number.isFinite(subtotal) ? subtotal : 0;
  const hasStaleSubtotal = !hasLines && subtotalCents > 0;

  return (
    <div className="qd-doc print-root" data-testid="quote-document">
      {/* PT-15 #8d (2026-07-24): the opt.headlineNotes render block was removed
          along with the toggle it depended on. Quote-level notes render only
          at the wire-canonical bottom-of-doc location (governed by `notes`). */}

      <DocumentHeader
        branding={branding}
        profile={profile}
        meta={{ quoteNumber, date, validUntil, salesperson }}
      />

      <PartiesBlock
        billTo={billTo}
        jobAddress={jobAddress}
        sameAsBilling={sameAsBilling}
        paymentTerms={paymentTerms}
        paymentType={paymentType}
      />

      {/* DEF-088 (W1.1a): render EmptyState instead of a bare <thead> when the
          quote has no line items. Copy per W0.1b ruling. */}
      {hasLines ? (
        <ScopeTable
          groups={groups}
          showSqft={opt.showSqft !== false}
          showLineItemPricing={opt.lineItemPricing !== false}
          showLaborPricing={!!opt.laborPricing}
          showAreaColumn={opt.groupByArea === false}
        />
      ) : (
        <div data-testid="quote-document-line-items-empty">
          <EmptyState mode="first-run" title="No line items on this quote." />
        </div>
      )}

      {/* DEF-088 (W1.1b): gate totals block. Three cases:
            • hasLines                     → render totals normally
            • !hasLines && subtotal > 0    → render inconsistency notice + subtotal AS STORED
            • !hasLines && subtotal === 0  → suppress totals (no contradiction)
          Alert receives NO icon child — Alert owns its own variant icon (DEF-090). */}
      {hasLines && (
        <TotalsBlock
          subtotal={subtotal}
          discount={discount}
          discounts={discounts}
          tax={tax}
          taxLabel={taxLabel}
          total={total}
          showTax={opt.showTax !== false}
        />
      )}
      {hasStaleSubtotal && (
        <>
          <div data-testid="quote-document-inconsistency-notice">
            <Alert variant="warning">
              This quote has a subtotal but no line items. The record may be incomplete — contact support before sending.
            </Alert>
          </div>
          <div className="qd-totals" data-testid="quote-document-stale-subtotal">
            <div className="qd-trow qd-gt"><span>Subtotal</span><span className="qd-v">{fmtCents(subtotal)}</span></div>
          </div>
        </>
      )}

      <OptionsBlock options={optionItems} />

      {/* PR-20 (2026-07-22): Notes block on the customer document — between
          totals/options and Terms. Wire QR-1 R1: notes print HERE when the
          display toggle is on, not only in the rail. */}
      {opt.notes !== false && notes && (
        <>
          <div className="qd-docsec">Notes</div>
          <div className="qd-customer-notes">{notes}</div>
        </>
      )}

      {/* Terms & conditions moved BELOW signature per Q-B2 (\u00a73al ruling
          via Lindsey, 08-18 session): spray-foam T&C run long and push the
          signature line offscreen if T&C prints above it. Attachments still
          print above the signature. */}

      {/* Attachments \u2014 opt-in via toggle; list only, filenames + optional description. */}
      {opt.attachments && attachments && attachments.length > 0 && (
        <>
          <div className="qd-docsec">Attachments</div>
          <ul className="qd-attachments">
            {attachments.map((a) => (
              <li key={a.id || a.name}>
                {a.name}
                {a.description && <span className="qd-mut">{' \u2014 '}{a.description}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Signature block */}
      <div className="qd-sigrow">
        <div><div className="qd-sigline" /><div className="qd-siglab">Customer signature</div></div>
        <div><div className="qd-sigline" /><div className="qd-siglab">Date</div></div>
      </div>

      {/* Terms & conditions \u2014 the selected template's body prints in full.
          Moved BELOW signature per Q-B2 (\u00a73al ruling via Lindsey, 08-18 session)
          so spray-foam T&C do not push the signature line offscreen. */}
      {terms && (
        <>
          <div className="qd-docsec">Terms &amp; conditions</div>
          <div className="qd-terms" dangerouslySetInnerHTML={{ __html: terms }} />
        </>
      )}
    </div>
  );
}
